import { useCallback } from 'react';

import { ParameterMetadata, ParameterType, PrimitiveType } from '../../../api/api';
import { EdgeType } from '../Models/EdgeTypes';
import { FlowDefinitionModel, FlowEdgeModel } from '../Models/FlowModel';
import { ActivityNode, TransitionEdge } from '../Models/GraphModel';
import { AgentToolInputBindings } from './AgentToolInputBindings';
import { VariableScope } from '../Models/VariableScopes/VariableScope';
import { GenericProperties } from './GenericProperties';
import { AGENT_TOOL_TRANSITION_TYPE } from '../Models/FlowModel';

const AGENT_TOOL_CATEGORY = 'Agent Tool';

type TransitionPropertiesProps = {
	transition: TransitionEdge;
	toolActivity?: ActivityNode;
	toolInputVariableScope?: VariableScope;
	isToolInputScopeLoading?: boolean;
	flow?: FlowDefinitionModel;
	onTransitionDataChanged?: (id: string, model: FlowEdgeModel) => void;
	variableScope?: VariableScope;
	isVariableScopeLoading?: boolean;
	readOnly?: boolean;
};

const CONDITION_PARAMETER_METADATA: ParameterMetadata = {
	name: 'Condition',
	displayName: 'Condition',
	type: ParameterType.Expression,
	categories: [],
	expectedType: { type: PrimitiveType.Boolean },
	isCollection: false,
	order: 0,
};

const ORDER_PARAMETER_METADATA: ParameterMetadata = {
	name: 'Order',
	displayName: 'Order',
	type: ParameterType.Primitive,
	categories: [],
	expectedType: { type: PrimitiveType.Integer },
	isCollection: false,
	defaultValue: 0,
	order: 1,
};

const EDGE_TYPE_PARAMETER_METADATA: ParameterMetadata = {
	name: 'Metadata.edgeType',
	displayName: 'Edge Type',
	type: ParameterType.Primitive,
	categories: [],
	expectedType: { type: PrimitiveType.String },
	isCollection: false,
	defaultValue: EdgeType.Default,
	order: 2,
};

const CAPTURE_TOOL_CALL_PARAMETER_METADATA: ParameterMetadata = {
	name: 'CaptureToolCallTraceMetadata',
	displayName: 'Capture Tool Call',
	type: ParameterType.Primitive,
	category: AGENT_TOOL_CATEGORY,
	categories: [],
	expectedType: { type: PrimitiveType.Boolean },
	isCollection: false,
	defaultValue: false,
	order: 3,
};

const TOOL_DESCRIPTION_PARAMETER_METADATA: ParameterMetadata = {
	name: 'Description',
	displayName: 'Tool Description',
	type: ParameterType.Primitive,
	category: AGENT_TOOL_CATEGORY,
	categories: [],
	expectedType: { type: PrimitiveType.String },
	isCollection: false,
	order: 2.5,
};

const CAPTURE_TOOL_RESPONSE_PARAMETER_METADATA: ParameterMetadata = {
	name: 'CaptureToolResponseTraceMetadata',
	displayName: 'Capture Tool Call Result',
	type: ParameterType.Primitive,
	category: AGENT_TOOL_CATEGORY,
	categories: [],
	expectedType: { type: PrimitiveType.Boolean },
	isCollection: false,
	defaultValue: false,
	order: 4,
};

const TRANSITION_PARAMETERS: ParameterMetadata[] = [CONDITION_PARAMETER_METADATA, ORDER_PARAMETER_METADATA, EDGE_TYPE_PARAMETER_METADATA];

export function TransitionProperties({ transition, toolActivity, toolInputVariableScope, isToolInputScopeLoading, flow, onTransitionDataChanged, variableScope, isVariableScopeLoading, readOnly }: TransitionPropertiesProps) {
	const handleModelChanged = useCallback(
		(model: FlowEdgeModel) => {
			onTransitionDataChanged?.(transition.id, model);
		},
		[transition.id, onTransitionDataChanged],
	);
	const isAgentToolTransition = transition.data?.Type?.toLowerCase() === AGENT_TOOL_TRANSITION_TYPE.toLowerCase();
	const parameters = isAgentToolTransition
		? [...TRANSITION_PARAMETERS, TOOL_DESCRIPTION_PARAMETER_METADATA, CAPTURE_TOOL_CALL_PARAMETER_METADATA, CAPTURE_TOOL_RESPONSE_PARAMETER_METADATA]
		: TRANSITION_PARAMETERS;

	return (
		<>
			{transition.data && (
				<GenericProperties
					id={transition.id}
					name="Transition"
					parameters={parameters}
					model={transition.data}
					onModelChanged={handleModelChanged}
					variableScope={variableScope}
					isVariableScopeLoading={isVariableScopeLoading}
					readOnly={readOnly}
				/>
			)}
			{isAgentToolTransition && toolActivity && flow && (
				<AgentToolInputBindings transition={transition} activity={toolActivity} flow={flow} variableScope={toolInputVariableScope}
					readOnly={readOnly || isVariableScopeLoading || isToolInputScopeLoading} onChange={onTransitionDataChanged} />
			)}
		</>
	);
}
